import { FC, useEffect, useState } from 'react';
import { Box, Fade, Typography, Grid } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormikContext } from 'formik';
import SearchIcon from '@mui/icons-material/Search';
import { VEHICLE_DETAILS_PART_ONE_ERROR_MESSAGES } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import LicencePlateTextField from '@acorn-portal/shared/src/components/inputs/LicencePlateTextField';
import { isEmptyObject } from '@acorn-portal/shared/src/utils';
import { getVehicleDetailsByVrm, updateResult } from '@acorn-portal/shared/src/store/vehicleLookup.slice';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { unwrapResult } from '@reduxjs/toolkit';

interface IFormState {
	vrm: string;
}

const vehicleDetailsStyle = {
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	textAlign: 'right',
};

const VehicleLookup: FC = () => {
	const vehicle = useAppSelector((redux) => redux.vehicleLookup);

	const formik = useFormikContext<IFormState>();
	const dispatch = useAppDispatch();
	const [loading, setLoading] = useState(false);

	const onVehicleLookUp = async () => {
		formik.setFieldTouched('vrm', true, true);
		const isValid = await formik.validateForm();
		if ((!formik.dirty && formik.values.vrm === '') || (!isEmptyObject(isValid) && isValid.vrm !== undefined)) return;
		setLoading(true);
		try {
			const actionResponse = await dispatch(getVehicleDetailsByVrm(formik.values.vrm.toUpperCase()));
			const response = unwrapResult(actionResponse);
			const isMotorcycle = response.isMotorCycle ?? false;
			if (isMotorcycle) {
				dispatch(updateResult({ vehicleInfo: {} }));
				formik.setFieldError('vrm', VEHICLE_DETAILS_PART_ONE_ERROR_MESSAGES.motorCyclesNotInsurable);
				return;
			}
			if (response?.vehicleInfo.value && response.vehicleInfo.value > 0) {
				formik.setFieldValue('value', response.vehicleInfo.value);
			}
		} catch (e) {
			console.error('Error fetching vehicle details:', e);
			formik.setFieldError('vrm', VEHICLE_DETAILS_PART_ONE_ERROR_MESSAGES.registrationApiError);
			return;
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (formik.values.vrm === '' && !isEmptyObject(vehicle.vehicleInfo)) {
			dispatch(updateResult({ vehicleInfo: {} }));
		}
	}, []);

	return (
		<Box
			sx={(theme) => ({
				pr: '20%',
				[theme.breakpoints.down('md')]: {
					pr: 'unset',
				},
			})}
		>
			<Box
				sx={(theme) => ({
					overflow: 'hidden',
					'& button': {
						my: 2,
						float: 'right',
						[theme.breakpoints.down('md')]: {
							width: '100%',
						},
					},
				})}
			>
				<LicencePlateTextField
					id='vrm'
					name='vrm'
					error={formik.touched.vrm && Boolean(formik.errors.vrm)}
					helperText={formik.touched.vrm && formik.errors.vrm}
					onChange={formik.handleChange}
					value={formik.values.vrm}
					onBlur={formik.handleBlur}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							onVehicleLookUp();
						}
					}}
				/>
				<LoadingButton variant='contained' onClick={() => onVehicleLookUp()} loading={loading} endIcon={<SearchIcon />}>
					Find Vehicle
				</LoadingButton>
			</Box>
			{!isEmptyObject(vehicle.vehicleInfo) && (
				<Fade in={!isEmptyObject(vehicle.vehicleInfo)}>
					<Grid container direction='row' spacing={1} sx={{ my: 2 }}>
						<Grid
							size={{ xs: 12, md: 4 }}
							sx={(theme) => ({
								textAlign: 'right',
								[theme.breakpoints.down('md')]: {
									textAlign: 'left',
								},
							})}
						>
							<Typography>Vehicle Details</Typography>
						</Grid>
						<Grid size={{ xs: 12, md: 8 }}>
							<Box sx={{ border: '2px solid black', borderRadius: 4, p: 2 }}>
								<Typography sx={vehicleDetailsStyle}>
									<Box component='span' fontWeight='bold'>
										Make
									</Box>
									{vehicle.vehicleInfo.make?.code}
								</Typography>
								<Typography sx={vehicleDetailsStyle}>
									<Box component='span' fontWeight='bold'>
										Model
									</Box>
									{vehicle.vehicleInfo.model?.code}
								</Typography>
								<Typography sx={vehicleDetailsStyle}>
									<Box component='span' fontWeight='bold'>
										Type
									</Box>
									{`${vehicle.vehicleInfo.numberOfDoors?.code as string} Door ${
										vehicle.vehicleInfo.bodyType?.desc as string
									}`}
								</Typography>
								<Typography sx={vehicleDetailsStyle}>
									<Box component='span' fontWeight='bold'>
										CC
									</Box>
									{vehicle.vehicleInfo.cubicCapacity}
								</Typography>
								<Typography sx={vehicleDetailsStyle}>
									<Box component='span' fontWeight='bold'>
										Fuel Type
									</Box>
									{vehicle.vehicleInfo.fuelType?.desc}
								</Typography>

								<Typography sx={vehicleDetailsStyle}>
									<Box component='span' fontWeight='bold'>
										Transmission
									</Box>
									{vehicle.vehicleInfo.transmission?.code}
								</Typography>
							</Box>
						</Grid>
					</Grid>
				</Fade>
			)}
		</Box>
	);
};

export default VehicleLookup;
